import React, { useEffect, useState, useRef } from 'react';
import { useHospitalSelection } from '../../hooks/useHospitalSelection';
import { usePersistedState } from '../../hooks/usePersistedState';
import LineChart from '../../components/Charts/LineChart';
import CustomSelect from '../../components/Common/CustomSelect';
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider';
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice';
import { generatePDFIds } from '../../utils/pdfIdHelper';
import { parseMonthToValue, safeNumber } from '../../utils/dataUtils';
import { useTmbSlider } from '../../context/TmbSliderContext';
import {
    generateMonthListFromObjects,
    initializeSliderValues,
    getSliderIndicesFromValues,
    TMB_SLIDER_KEY
} from '../../utils/tmbUtils';
import html2canvas from "html2canvas";
import { exportToCSV } from '../../utils/exportUtils';
import '../BCBS/OutcomeMeasures.css';

function parseQuarterlyPeriod(raw: any): { year: number; quarter: number } | null {
    const s = String(raw ?? '').trim();
    let m = s.match(/Q\s*(\d)\s+(\d{4})/i);
    if (m) return { quarter: parseInt(m[1], 10), year: parseInt(m[2], 10) };
    m = s.match(/(\d{4})\s*[- ]?\s*Q\s*(\d)/i);
    if (m) return { year: parseInt(m[1], 10), quarter: parseInt(m[2], 10) };
    m = s.match(/Q\s*(\d)\s*[- ]\s*(\d{4})/i);
    if (m) return { quarter: parseInt(m[1], 10), year: parseInt(m[2], 10) };
    return null;
}

function quarterlySortKey(raw: any): number {
    const p = parseQuarterlyPeriod(raw);
    if (!p) return 0;
    return p.year * 10 + p.quarter;
}

function parseBiYearlyPeriod(raw: any): { year: number; secondHalf: boolean } | null {
    const s = String(raw ?? '').trim();
    const m = s.match(/(Jan-Jun|Jul-Dec)\s+(\d{4})/i);
    if (!m) return null;
    return { year: parseInt(m[2], 10), secondHalf: /^jul/i.test(m[1]) };
}

function biYearlySortKey(raw: any): number {
    const p = parseBiYearlyPeriod(raw);
    if (!p) return 0;
    return p.year * 2 + (p.secondHalf ? 1 : 0);
}

function yearlySortKey(raw: any): number {
    const y = parseInt(String(raw ?? '').trim(), 10);
    return Number.isFinite(y) ? y : 0;
}

function quarterStartMonthValue(raw: any): number {
    const p = parseQuarterlyPeriod(raw);
    if (!p) return 0;
    return p.year * 100 + (p.quarter - 1) * 3 + 1;
}

function quarterEndMonthValue(raw: any): number {
    const p = parseQuarterlyPeriod(raw);
    if (!p) return 0;
    return p.year * 100 + p.quarter * 3;
}

function biYearlyStartMonthValue(raw: any): number {
    const p = parseBiYearlyPeriod(raw);
    if (!p) return 0;
    return p.year * 100 + (p.secondHalf ? 7 : 1);
}

function biYearlyEndMonthValue(raw: any): number {
    const p = parseBiYearlyPeriod(raw);
    if (!p) return 0;
    return p.year * 100 + (p.secondHalf ? 12 : 6);
}

function yearlyStartMonthValue(raw: any): number {
    const y = parseInt(String(raw ?? '').trim(), 10);
    if (!Number.isFinite(y)) return 0;
    return y * 100 + 1;
}

function yearlyEndMonthValue(raw: any): number {
    const y = parseInt(String(raw ?? '').trim(), 10);
    if (!Number.isFinite(y)) return 0;
    return y * 100 + 12;
}

function periodStartMonthValue(periodType: string, raw: any): number {
    if (periodType === 'quarterly') return quarterStartMonthValue(raw);
    if (periodType === 'bi-yearly') return biYearlyStartMonthValue(raw);
    if (periodType === 'yearly') return yearlyStartMonthValue(raw);
    return parseMonthToValue(raw);
}

function periodEndMonthValue(periodType: string, raw: any): number {
    if (periodType === 'quarterly') return quarterEndMonthValue(raw);
    if (periodType === 'bi-yearly') return biYearlyEndMonthValue(raw);
    if (periodType === 'yearly') return yearlyEndMonthValue(raw);
    return parseMonthToValue(raw);
}

function periodInSliderRange(periodType: string, raw: any, sliderStart: number, sliderEnd: number): boolean {
    if (!sliderStart || !sliderEnd) return true;
    const startVal = periodStartMonthValue(periodType, raw);
    const endVal = periodEndMonthValue(periodType, raw);
    if (!startVal || !endVal) return true;
    return endVal >= sliderStart && startVal <= sliderEnd;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildMonthlyRange(startVal: number, endVal: number): string[] {
    if (!startVal || !endVal || startVal > endVal) return [];
    const out: string[] = [];
    let ym = startVal;
    while (ym <= endVal) {
        const y = Math.floor(ym / 100);
        const m = ym % 100;
        out.push(`${MONTH_NAMES[m - 1]} ${y}`);
        if (m === 12) ym = (y + 1) * 100 + 1;
        else ym = y * 100 + (m + 1);
    }
    return out;
}

function buildQuarterRange(startVal: number, endVal: number): string[] {
    if (!startVal || !endVal || startVal > endVal) return [];
    const startYear = Math.floor(startVal / 100);
    const startQuarter = Math.floor(((startVal % 100) - 1) / 3) + 1;
    const endYear = Math.floor(endVal / 100);
    const endQuarter = Math.floor(((endVal % 100) - 1) / 3) + 1;
    const out: string[] = [];
    for (let y = startYear; y <= endYear; y++) {
        const qStart = y === startYear ? startQuarter : 1;
        const qEnd = y === endYear ? endQuarter : 4;
        for (let q = qStart; q <= qEnd; q++) out.push(`Q${q} ${y}`);
    }
    return out;
}

function buildBiYearlyRange(startVal: number, endVal: number): string[] {
    if (!startVal || !endVal || startVal > endVal) return [];
    const startYear = Math.floor(startVal / 100);
    const startSecondHalf = (startVal % 100) >= 7;
    const endYear = Math.floor(endVal / 100);
    const endSecondHalf = (endVal % 100) >= 7;
    const startOrd = startYear * 2 + (startSecondHalf ? 1 : 0);
    const endOrd = endYear * 2 + (endSecondHalf ? 1 : 0);
    const out: string[] = [];
    for (let ord = startOrd; ord <= endOrd; ord++) {
        const y = Math.floor(ord / 2);
        const secondHalf = ord % 2 === 1;
        out.push(secondHalf ? `Jul-Dec ${y}` : `Jan-Jun ${y}`);
    }
    return out;
}

function buildYearlyRange(startVal: number, endVal: number): string[] {
    if (!startVal || !endVal || startVal > endVal) return [];
    const startYear = Math.floor(startVal / 100);
    const endYear = Math.floor(endVal / 100);
    const out: string[] = [];
    for (let y = startYear; y <= endYear; y++) out.push(String(y));
    return out;
}

function buildCategoriesForRange(periodType: string, startVal: number, endVal: number): string[] {
    switch (periodType) {
        case 'quarterly': return buildQuarterRange(startVal, endVal);
        case 'bi-yearly': return buildBiYearlyRange(startVal, endVal);
        case 'yearly': return buildYearlyRange(startVal, endVal);
        default: return buildMonthlyRange(startVal, endVal);
    }
}

function canonicalPeriodLabel(periodType: string, raw: any): string {
    if (periodType === 'quarterly') {
        const p = parseQuarterlyPeriod(raw);
        return p ? `Q${p.quarter} ${p.year}` : String(raw ?? '');
    }
    if (periodType === 'bi-yearly') {
        const p = parseBiYearlyPeriod(raw);
        if (!p) return String(raw ?? '');
        return p.secondHalf ? `Jul-Dec ${p.year}` : `Jan-Jun ${p.year}`;
    }
    if (periodType === 'yearly') {
        const y = parseInt(String(raw ?? '').trim(), 10);
        return Number.isFinite(y) ? String(y) : String(raw ?? '');
    }
    return String(raw ?? '');
}

const OutcomeMeasures: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection();
    const { globalMonthList } = useTmbSlider();
    const [chartData, setChartData] = useState<any>({
        chart1: { categories: [], series: [] },
        chart2: { categories: [], series: [] },
        chart3: { categories: [], series: [] }
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [timePeriod, setTimePeriod] = usePersistedState<string>('tmb_outcome_measures_timePeriod', 'monthly');
    const [rawChartData, setRawChartData] = useState<any>(null);
    const [rawRaceEthnicityData, setRawRaceEthnicityData] = useState<any>(null);

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`;
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0]);
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange);
    const sliderStart = sliderRange[0] || 0;
    const sliderEnd = sliderRange[1] || 0;

    const chart1Ref = useRef<any>(null);
    const chart2Ref = useRef<any>(null);
    const chart3Ref = useRef<any>(null);
    const chart5Ref = useRef<any>(null);

    const [screeningChartData, setScreeningChartData] = useState<any>({ categories: [], series: [] });
    const [rawScreeningData, setRawScreeningData] = useState<any>(null);

    useEffect(() => {
        if (selectedHospitalIds.length === 0) {
            setChartData({
                chart1: { categories: [], series: [] },
                chart2: { categories: [], series: [] },
                chart3: { categories: [], series: [] }
            });
            setRawChartData(null);
            setRawRaceEthnicityData(null);
            setRawScreeningData(null);
            setInitialLoading(false);
            return;
        }

        setLoading(true);
        const hospitalIdsParam = selectedHospitalIds.join(',');
        const chartUrl = `/api/outcome/${hospitalIdsParam}`;
        const raceEthnicityUrl = `/api/outcome_race_ethnicity/${hospitalIdsParam}`;
        const screeningUrl = `/api/pmh/process/screening/${hospitalIdsParam}`;

        Promise.all([
            fetch(chartUrl).then(res => res.ok ? res.json() : null),
            fetch(raceEthnicityUrl).then(res => res.ok ? res.json() : null),
            fetch(screeningUrl).then(res => res.ok ? res.json() : null)
        ]).then(([chartRes, raceEthnicityRes, screeningRes]) => {
            setRawChartData(chartRes);
            setRawRaceEthnicityData(raceEthnicityRes);
            setRawScreeningData(screeningRes);

            const monthlyRows: { date: string }[] = [];
            const chartRows = chartRes?.response?.result?.rows || [];
            const chartCols = chartRes?.response?.result?.column_order || [];
            const chartReportIdx = chartCols.indexOf('report_type') !== -1 ? chartCols.indexOf('report_type') : 14;
            const chartPeriodIdx = chartCols.indexOf('Month') !== -1 ? chartCols.indexOf('Month') : 1;
            chartRows.forEach((row: any[]) => {
                if (row[chartReportIdx] === 'Monthly' && row[chartPeriodIdx]) {
                    monthlyRows.push({ date: String(row[chartPeriodIdx]) });
                }
            });

            const months = generateMonthListFromObjects(monthlyRows, (row) => row.date);
            if (months.length > 0) {
                setSliderRange(prev => initializeSliderValues(months, prev, 12));
            }

            setLoading(false);
            setInitialLoading(false);
        }).catch(err => {
            console.error("Error fetching outcome measures:", err);
            setLoading(false);
            setInitialLoading(false);
        });

    }, [selectedHospitalIds, refreshKey]);

    useEffect(() => {
        if (globalMonthList.length > 0) {
            setSliderRange(prev => initializeSliderValues(globalMonthList, prev, 12));
        }
    }, [globalMonthList]);

    useEffect(() => {
        if (rawChartData === null) return;
        if (!sliderStart || !sliderEnd) return;

        if (isMultiSelect) {
            processMultiSelectData(rawChartData, timePeriod);
        } else {
            processSingleSelectData(rawChartData, timePeriod);
        }
    }, [timePeriod, rawChartData, isMultiSelect, sliderStart, sliderEnd]);

    useEffect(() => {
        if (!rawRaceEthnicityData) return
        if (!sliderStart || !sliderEnd) return

        const rows = rawRaceEthnicityData?.response?.result?.rows || []
        const columns = rawRaceEthnicityData?.response?.result?.column_order || []

        if (rows.length === 0) {
            setChartData((prev: any) => ({ ...prev, chart2: { categories: [], series: [] } }))
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const colMonth = columns.indexOf('Month') !== -1 ? columns.indexOf('Month') : 3
        const colReportType = columns.indexOf('report_type') !== -1 ? columns.indexOf('report_type') : 4
        const colGroup = columns.indexOf('group') !== -1 ? columns.indexOf('group') : 5
        const colNumerator = columns.indexOf('numerator') !== -1 ? columns.indexOf('numerator') : 6
        const colDenominator = columns.indexOf('denominator') !== -1 ? columns.indexOf('denominator') : 7
        const colRaceType = columns.indexOf('race_ethnicity_type')

        // Only the binary stratification feeds this chart: 'All Other Groups' => BIPOC, plus 'NH White'.
        // NH White also appears under race_ethnicity_type='multi'; exclude it so it is not double-counted.
        const resolveGroup = (row: any[]): string => {
            const raw = String(row[colGroup] ?? '')
            const raceType = colRaceType !== -1 ? String(row[colRaceType] ?? '') : ''
            if (!/^binary$/i.test(raceType)) return ''
            if (/^all\s*other\s*groups$/i.test(raw)) return 'BIPOC'
            return raw
        }

        const filtered = rows.filter((row: any[]) =>
            row[colReportType] === intervalMap[timePeriod] &&
            periodInSliderRange(timePeriod, row[colMonth], sliderStart, sliderEnd)
        )

        if (filtered.length === 0) {
            setChartData((prev: any) => ({ ...prev, chart2: { categories: [], series: [] } }))
            return
        }

        const groups = ['BIPOC', 'NH White']

        const buildExpandedCategories = () => buildCategoriesForRange(timePeriod, sliderStart, sliderEnd)

        if (isMultiSelect) {
            const aggregated: Record<string, Record<string, { numSum: number, denSum: number }>> = {}

            filtered.forEach((row: any[]) => {
                const rawPeriod = String(row[colMonth] ?? '')
                const group = resolveGroup(row)
                const num = parseFloat(row[colNumerator])
                const den = parseFloat(row[colDenominator])

                if (!rawPeriod || !groups.includes(group) || isNaN(num) || isNaN(den)) return

                const period = timePeriod !== 'monthly'
                    ? canonicalPeriodLabel(timePeriod, rawPeriod)
                    : rawPeriod

                if (!aggregated[period]) aggregated[period] = {}
                if (!aggregated[period][group]) aggregated[period][group] = { numSum: 0, denSum: 0 }
                aggregated[period][group].numSum += num
                aggregated[period][group].denSum += den
            })

            const expandedCategories = buildExpandedCategories()

            const series = groups.map(group => ({
                name: group,
                data: expandedCategories.map(period => {
                    const d = aggregated[period]?.[group]
                    if (!d || d.denSum === 0) return null
                    return Math.round((d.numSum / d.denSum) * 100 * 100) / 100
                })
            }))

            setChartData((prev: any) => ({
                ...prev,
                chart2: { categories: expandedCategories, series }
            }))
        } else {
            const aggregated: Record<string, Record<string, { numSum: number, denSum: number }>> = {}

            filtered.forEach((row: any[]) => {
                const rawPeriod = String(row[colMonth] ?? '')
                const group = resolveGroup(row)
                const num = parseFloat(row[colNumerator])
                const den = parseFloat(row[colDenominator])

                if (!rawPeriod || !groups.includes(group) || isNaN(num) || isNaN(den)) return

                const period = timePeriod !== 'monthly'
                    ? canonicalPeriodLabel(timePeriod, rawPeriod)
                    : rawPeriod

                if (!aggregated[period]) aggregated[period] = {}
                if (!aggregated[period][group]) aggregated[period][group] = { numSum: 0, denSum: 0 }
                aggregated[period][group].numSum += num
                aggregated[period][group].denSum += den
            })

            const expandedCategories = buildExpandedCategories()

            const series = groups.map(group => ({
                name: group,
                data: expandedCategories.map(period => {
                    const d = aggregated[period]?.[group]
                    if (!d || d.denSum === 0) return null
                    return Math.round((d.numSum / d.denSum) * 100 * 100) / 100
                })
            }))

            setChartData((prev: any) => ({
                ...prev,
                chart2: { categories: expandedCategories, series }
            }))
        }
    }, [rawRaceEthnicityData, timePeriod, isMultiSelect, sliderStart, sliderEnd])

    useEffect(() => {
        if (!rawScreeningData) {
            setScreeningChartData({ categories: [], series: [] })
            return
        }
        if (!sliderStart || !sliderEnd) return

        const rows = rawScreeningData?.response?.result?.rows || []
        const columns = rawScreeningData?.response?.result?.column_order || []

        if (rows.length === 0) {
            setScreeningChartData({ categories: [], series: [] })
            return
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const reportTypeIdx = columns.indexOf('report_type') !== -1 ? columns.indexOf('report_type') : 2
        const stratIdx = columns.indexOf('stratification') !== -1 ? columns.indexOf('stratification') : 3
        const stratValueIdx = columns.indexOf('strat_value') !== -1 ? columns.indexOf('strat_value') : 4
        const periodLabelIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2

        const filteredRows = rows.filter((row: any[]) => {
            if (row[reportTypeIdx] !== intervalMap[timePeriod]) return false
            if (row[stratIdx] !== 'Overall') return false
            if (row[stratValueIdx] !== 'All Patients') return false
            const rawPeriod = String(row[periodLabelIdx] ?? '')
            if (/baseline/i.test(rawPeriod)) return true
            return periodInSliderRange(timePeriod, rawPeriod, sliderStart, sliderEnd)
        })

        if (filteredRows.length === 0) {
            setScreeningChartData({ categories: [], series: [] })
            return
        }

        const periodIdx = columns.indexOf('period_label') !== -1 ? columns.indexOf('period_label') : 2

        const aggregatedData: Record<string, {
            anxietyNum: number, depressionNum: number, denominator: number
        }> = {}

        filteredRows.forEach((row: any[]) => {
            const rawPeriod = String(row[periodIdx])
            if (!rawPeriod) return

            const denom = safeNumber(row[5])
            const anxNum = safeNumber(row[6])
            const depNum = safeNumber(row[7])

            if (denom === null || denom === 0) return

            const period = /baseline/i.test(rawPeriod)
                ? rawPeriod
                : (timePeriod !== 'monthly' ? canonicalPeriodLabel(timePeriod, rawPeriod) : rawPeriod)

            if (!aggregatedData[period]) {
                aggregatedData[period] = { anxietyNum: 0, depressionNum: 0, denominator: 0 }
            }
            aggregatedData[period].denominator += denom
            if (anxNum !== null) aggregatedData[period].anxietyNum += anxNum
            if (depNum !== null) aggregatedData[period].depressionNum += depNum
        })

        const allKeys = Object.keys(aggregatedData)
        const baselineKeys = allKeys.filter(k => /baseline/i.test(k))

        const regularRange = buildCategoriesForRange(timePeriod, sliderStart, sliderEnd)
        const expandedCategories = [...baselineKeys.sort(), ...regularRange]

        const anxietySeries: (number | null)[] = []
        const depressionSeries: (number | null)[] = []

        expandedCategories.forEach(period => {
            const d = aggregatedData[period]
            if (!d || d.denominator === 0) {
                anxietySeries.push(null)
                depressionSeries.push(null)
                return
            }

            const anxPct = (d.anxietyNum / d.denominator) * 100
            const depPct = (d.depressionNum / d.denominator) * 100

            anxietySeries.push(Math.round(anxPct * 100) / 100)
            depressionSeries.push(Math.round(depPct * 100) / 100)
        })

        setScreeningChartData({
            categories: expandedCategories,
            series: [
                { name: 'Anxiety Screening', data: anxietySeries },
                { name: 'Depression Screening', data: depressionSeries }
            ]
        })
    }, [rawScreeningData, timePeriod, isMultiSelect, sliderStart, sliderEnd]);

    const filterRowsByTimePeriod = (rows: any[], period: string, typeColIndex: number) => {
        return rows.filter((row: any) => {
            const reportType = row[typeColIndex];
            switch (period) {
                case 'monthly': return reportType === 'Monthly';
                case 'quarterly': return reportType === 'Quarterly';
                case 'bi-yearly': return reportType === 'Bi-Yearly';
                case 'yearly': return reportType === 'Yearly';
                default: return reportType === 'Monthly';
            }
        });
    };

    const sortRows = (rows: any[], period: string, periodColIndex: number) => {
        return rows.sort((a: any, b: any) => {
            const periodA = a[periodColIndex];
            const periodB = b[periodColIndex];

            if (period === 'monthly') {
                return parseMonthToValue(periodA) - parseMonthToValue(periodB);
            } else if (period === 'quarterly') {
                return quarterlySortKey(periodA) - quarterlySortKey(periodB);
            } else if (period === 'bi-yearly') {
                return biYearlySortKey(periodA) - biYearlySortKey(periodB);
            } else if (period === 'yearly') {
                return yearlySortKey(periodA) - yearlySortKey(periodB);
            }
            return 0;
        });
    }

    const processSingleSelectData = (data: any, period: string) => {
        if (!data) return;
        const rows = data.response?.result?.rows || [];
        const columns = data.response?.result?.column_order || [];

        const getColIndex = (name: string) => columns.indexOf(name);

        const reportTypeIdx = getColIndex('report_type') !== -1 ? getColIndex('report_type') : 14;
        let filtered = filterRowsByTimePeriod(rows, period, reportTypeIdx);

        const periodColIdx = getColIndex('Month') !== -1 ? getColIndex('Month') : 1;
        filtered = filtered.filter((r: any) =>
            periodInSliderRange(period, r[periodColIdx], sliderStart, sliderEnd)
        );

        filtered = sortRows(filtered, period, periodColIdx);

        const categories: string[] = buildCategoriesForRange(period, sliderStart, sliderEnd);
        const rowByPeriod = new Map<string, any>();
        for (const r of filtered) {
            const key = canonicalPeriodLabel(period, r[periodColIdx]);
            if (!rowByPeriod.has(key)) rowByPeriod.set(key, r);
        }

        const colNTSV = getColIndex('Cesarean_Rate') !== -1 ? getColIndex('Cesarean_Rate') : 9;
        const colPrivate = getColIndex('Private') !== -1 ? getColIndex('Private') : 7;
        const colPublic = getColIndex('Public') !== -1 ? getColIndex('Public') : 8;
        const colUninsured = getColIndex('Uninsured') !== -1 ? getColIndex('Uninsured') : 13;
        const colUnknown = getColIndex('Unknown_Insurance') !== -1 ? getColIndex('Unknown_Insurance') :
            (getColIndex('Unknown') !== -1 ? getColIndex('Unknown') : 10);

        const pick = (colIdx: number) =>
            categories.map((cat) => {
                const r = rowByPeriod.get(cat);
                return r ? safeNumber(r[colIdx]) : null;
            });

        const series1 = [{ name: 'NTSV Cesarean Rate %', data: pick(colNTSV) }];

        const series3 = [
            { name: 'Private', data: pick(colPrivate) },
            { name: 'Public', data: pick(colPublic) },
            { name: 'Uninsured', data: pick(colUninsured) },
            { name: 'Unknown Insurance', data: pick(colUnknown) }
        ];

        setChartData((prev: any) => ({
            ...prev,
            chart1: { categories, series: series1 },
            chart3: { categories, series: series3 }
        }));
    };

    const processMultiSelectData = (data: any, period: string) => {
        if (!data) return;
        const rows = data.response?.result?.rows || [];
        const columns = data.response?.result?.column_order || [];

        const getColIndex = (name: string) => columns.indexOf(name);

        const reportTypeIdx = getColIndex('report_type') !== -1 ? getColIndex('report_type') : 14;
        const periodColIdx = getColIndex('Month') !== -1 ? getColIndex('Month') : 1;
        const colNTSV = getColIndex('Cesarean_Rate') !== -1 ? getColIndex('Cesarean_Rate') : 9;
        const colPrivate = getColIndex('Private') !== -1 ? getColIndex('Private') : 7;
        const colPublic = getColIndex('Public') !== -1 ? getColIndex('Public') : 8;
        const colUninsured = getColIndex('Uninsured') !== -1 ? getColIndex('Uninsured') : 13;
        const colUnknown = getColIndex('Unknown_Insurance') !== -1 ? getColIndex('Unknown_Insurance') :
            (getColIndex('Unknown') !== -1 ? getColIndex('Unknown') : 10);

        let filtered = filterRowsByTimePeriod(rows, period, reportTypeIdx);
        filtered = filtered.filter((r: any) =>
            periodInSliderRange(period, r[periodColIdx], sliderStart, sliderEnd)
        );

        const grouped: { [key: string]: any[] } = {};
        filtered.forEach((r: any) => {
            const key = canonicalPeriodLabel(period, r[periodColIdx]);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(r);
        });

        const sortedPeriods = buildCategoriesForRange(period, sliderStart, sliderEnd);

        const avg = (items: any[], idx: number) => {
            if (!items || items.length === 0) return null;
            const values = items.map(i => parseFloat(i[idx]));
            const valid = values.filter(v => !isNaN(v));
            if (valid.length === 0) return null;
            const sum = valid.reduce((a, b) => a + b, 0);
            return sum / items.length;
        };

        const avgForPeriod = (p: string, idx: number) => (grouped[p] ? avg(grouped[p], idx) : null);

        const series1 = [{ name: 'NTSV Cesarean Rate %', data: sortedPeriods.map(p => avgForPeriod(p, colNTSV)) }];

        const series3 = [
            { name: 'Private', data: sortedPeriods.map(p => avgForPeriod(p, colPrivate)) },
            { name: 'Public', data: sortedPeriods.map(p => avgForPeriod(p, colPublic)) },
            { name: 'Uninsured', data: sortedPeriods.map(p => avgForPeriod(p, colUninsured)) },
            { name: 'Unknown Insurance', data: sortedPeriods.map(p => avgForPeriod(p, colUnknown)) }
        ];

        setChartData((prev: any) => ({
            ...prev,
            chart1: { categories: sortedPeriods, series: series1 },
            chart3: { categories: sortedPeriods, series: series3 }
        }));
    };

    const ensurePoppinsLoaded = async () => {
        try {
            await Promise.all([
                (document as any).fonts?.load('400 12px "Poppins"'),
                (document as any).fonts?.load('500 14px "Poppins"'),
                (document as any).fonts?.load('600 16px "Poppins"'),
                (document as any).fonts?.load('700 18px "Poppins"'),
            ]);
            await (document as any).fonts?.ready;
        } catch {
        }
    };

    const handleDownloadPNG = async (ref: React.RefObject<HTMLDivElement>, filename: string) => {
        if (ref.current) {
            await ensurePoppinsLoaded();
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-ignore')
            } as any);
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        }
    };

    const handleDownloadSVG = async (ref: React.RefObject<HTMLDivElement>, title: string | undefined, filename: string) => {
        if (ref.current) {
            await ensurePoppinsLoaded();
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-menu') || el.classList.contains('export-dropdown') || el.classList.contains("export-ignore2")
            } as any);

            const width = canvas.width / 2;
            const height = canvas.height / 2;
            const dataURL = canvas.toDataURL('image/png');

            let svgTitleElement = '';
            let svgHeight = height;

            if (title) {
                const escapedTitle = title
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;');

                svgTitleElement = `<text x="${width / 2}" y="25" text-anchor="middle" class="chart-title">${escapedTitle}</text>`;
                svgHeight = height + 40;
            }

            const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${svgHeight}" viewBox="0 0 ${width} ${svgHeight}">
  <style>
    .chart-title {
      font-family: 'Poppins', 'Segoe UI', sans-serif;
      font-size: 16px;
      fill: #1a2a44;
      font-weight: bold;
    }
  </style>
  <rect width="100%" height="100%" fill="white"/>
  ${svgTitleElement}
  <image x="0" y="${title ? 40 : 0}" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;

            const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.svg`;
            link.click();
            URL.revokeObjectURL(url);
        }
    };

    const NTSVCesareanRateContainerRef = React.useRef<HTMLDivElement>(null)
    const NTSVcesareanStratifiedContainerRef = React.useRef<HTMLDivElement>(null)
    const chart3containerRef = React.useRef<HTMLDivElement>(null)
    const chart5ContainerRef = React.useRef<HTMLDivElement>(null)

    const handleChartCSVDownload = (chartKey: string, filename: string) => {
        const { categories, series } = chartData[chartKey] || {}
        if (!categories || categories.length === 0) return

        const seriesNames = series.map((s: any) => s.name)
        exportToCSV({
            headers: ['Month', ...seriesNames],
            rows: categories.map((cat: string, i: number) =>
                [cat, ...series.map((s: any) => s.data[i] !== null && s.data[i] !== undefined ? s.data[i] : '')]
            ),
            filename
        })
    }

    const handleScreeningCSVDownload = () => {
        const { categories, series } = screeningChartData || {}
        if (!categories || categories.length === 0) return

        const seriesNames = series.map((s: any) => s.name)
        exportToCSV({
            headers: ['Month', ...seriesNames],
            rows: categories.map((cat: string, i: number) =>
                [cat, ...series.map((s: any) => s.data[i] !== null && s.data[i] !== undefined ? s.data[i] : '')]
            ),
            filename: 'pmh_screening'
        })
    }

    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId);
        if (menu) menu.style.display = 'none';
    };

    if (initialLoading) {
        return <LoadingNotice title="Loading outcome measures data..." />;
    }

    const handleSliderChange = (startIndex: number, endIndex: number) => {
        setSliderRange([
            globalMonthList[startIndex]?.value || 0,
            globalMonthList[endIndex]?.value || 0
        ]);
    };

    return (
        <div id={generatePDFIds.dashboard()} className="bcbs-outcome-measures-page">
            <div className="page-header">
                <h1 className="page-title">Outcome Measures</h1>
                <div className="controls">
                    {globalMonthList.length > 0 && (
                        <div className="date-slider-container">
                            <DateRangeSlider
                                monthList={globalMonthList}
                                startIndex={sliderStartIdx}
                                endIndex={sliderEndIdx}
                                onChange={handleSliderChange}
                            />
                        </div>
                    )}
                    <CustomSelect
                        value={timePeriod}
                        onChange={setTimePeriod}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' }
                        ]}
                    />
                </div>
            </div>

            <div className="chart-grid">
                <div id={generatePDFIds.chart(1)} className="chart-container" ref={NTSVCesareanRateContainerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">Achieve NTSV Cesarean Rate at or below 25%</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-tmb-outcome-1');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-tmb-outcome-1" className="export-dropdown-menu" style={{ whiteSpace: 'nowrap' }}>
                                <div onClick={() => { handleDownloadSVG(NTSVCesareanRateContainerRef, undefined, "Achieve Healthy People NTSV Cesarean Rate at or below 23.6%"); closeMenu('export-dropdown-menu-tmb-outcome-1'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(NTSVCesareanRateContainerRef, "Achieve Healthy People NTSV Cesarean Rate at or below 23.6%"); closeMenu('export-dropdown-menu-tmb-outcome-1'); }}>Download PNG</div>
                                <div onClick={() => { handleChartCSVDownload('chart1', 'ntsv_cesarean_rate'); closeMenu('export-dropdown-menu-tmb-outcome-1'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart1Ref}
                        categories={chartData.chart1.categories}
                        series={chartData.chart1.series}
                        goalLine={25}
                        goalLabel="Goal ≤ 25%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={false}
                        yAxisLabel="NTSV Cesarean Rate %"
                        colors={["rgb(86,180,233)"]}
                        fontFamily="Poppins, Segoe UI, sans-serif"
                        legendPosition="top"
                        legendAlign="right"
                        showXAxisTooltip={true}
                        exportFilename="Achieve NTSV Cesarean Rate at or below 25%"
                        chartTitle="Achieve NTSV Cesarean Rate at or below 25%"
                        pageTitle="Outcome Measures"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                    />
                </div>

                <div id={generatePDFIds.chart(2)} className="chart-container" ref={NTSVcesareanStratifiedContainerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">Achieve NTSV Cesarean Rate for BIPOC (Black, Hispanic, Asian, Other) and NH white at or below 25%</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-tmb-outcome-2');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-tmb-outcome-2" className="export-dropdown-menu" style={{ whiteSpace: 'nowrap' }}>
                                <div onClick={() => { handleDownloadSVG(NTSVcesareanStratifiedContainerRef, undefined, "Achieve NTSV Cesarean Rate for BIPOC (Black, Hispanic, Asian, Other) and NH white at or below 25%"); closeMenu('export-dropdown-menu-tmb-outcome-2'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(NTSVcesareanStratifiedContainerRef, "Achieve Healthy People NTSV Cesarean Rate at or below 23.6%"); closeMenu('export-dropdown-menu-tmb-outcome-2'); }}>Download PNG</div>
                                <div onClick={() => { handleChartCSVDownload('chart2', 'ntsv_cesarean_rate_race_ethnicity'); closeMenu('export-dropdown-menu-tmb-outcome-2'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart2Ref}
                        categories={chartData.chart2.categories}
                        series={chartData.chart2.series}
                        goalLine={25}
                        goalLabel="Goal ≤ 25%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={false}
                        yAxisLabel="NTSV Cesarean Rate %"
                        colors={["#0072B1", "#F5EC77"]}
                        fontFamily="Poppins, Segoe UI, sans-serif"
                        legendPosition="top"
                        legendAlign="center"
                        showXAxisTooltip={true}
                        exportFilename="Achieve NTSV Cesarean Rate for BIPOC (Black, Hispanic, Asian, Other) and NH white at or below 25%"
                        chartTitle="Achieve NTSV Cesarean Rate for BIPOC (Black, Hispanic, Asian, Other) and NH white at or below 25%"
                        pageTitle="Outcome Measures"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                    />
                </div>

                <div id={generatePDFIds.chart(3)} className="chart-container" ref={chart3containerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-tmb-outcome-3');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-tmb-outcome-3" className="export-dropdown-menu" style={{ whiteSpace: 'nowrap' }}>
                                <div onClick={() => { handleDownloadSVG(chart3containerRef, undefined, 'Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance'); closeMenu('export-dropdown-menu-tmb-outcome-3'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(chart3containerRef, 'Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance'); closeMenu('export-dropdown-menu-tmb-outcome-3'); }}>Download PNG</div>
                                <div onClick={() => { handleChartCSVDownload('chart3', 'ntsv_cesarean_rate_insurance'); closeMenu('export-dropdown-menu-tmb-outcome-3'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart3Ref}
                        categories={chartData.chart3.categories}
                        series={chartData.chart3.series}
                        goalLine={25}
                        goalLabel="Goal ≤ 25%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={false}
                        yAxisLabel="NTSV Cesarean Rate"
                        colors={["rgb(0,114,178)", "rgb(230,159,0)", "rgb(204,121,167)", "rgb(213,94,0)"]}
                        fontFamily="Poppins, Segoe UI, sans-serif"
                        legendPosition="top"
                        legendAlign="center"
                        showXAxisTooltip={true}
                        exportFilename="Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance"
                        chartTitle="Achieve at or below 25% NTSV cesarean rate for all stratified groups by insurance"
                        pageTitle="Outcome Measures"
                        isLoading={loading}
                        showZeroLine={true}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                    />
                </div>
            </div>

            <div id={generatePDFIds.chart(5)} className="chart-container" ref={chart5ContainerRef}>
                <div className="chart-header-row">
                    <h2 className="chart-title">PMH Screening</h2>
                    <div className="export-menu export-ignore">
                        <button className="hamburger-btn" onClick={() => {
                            const menu = document.getElementById('export-dropdown-menu-tmb-outcome-5');
                            if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                        }}>&#9776;</button>
                        <div id="export-dropdown-menu-tmb-outcome-5" className="export-dropdown-menu" style={{ whiteSpace: 'nowrap' }}>
                            <div onClick={() => { handleDownloadSVG(chart5ContainerRef, undefined, "PMH Screening"); closeMenu('export-dropdown-menu-tmb-outcome-5'); }}>Download SVG</div>
                            <div onClick={() => { handleDownloadPNG(chart5ContainerRef, "PMH Screening"); closeMenu('export-dropdown-menu-tmb-outcome-5'); }}>Download PNG</div>
                            <div onClick={() => { handleScreeningCSVDownload(); closeMenu('export-dropdown-menu-tmb-outcome-5'); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p style={{ fontSize: '14px', color: '#555', margin: '0 0 1rem 1.2rem' }}>
                    Percent of sample patient charts that had documentation of PMH screening using a validated tool.
                </p>
                <LineChart
                    ref={chart5Ref}
                    categories={screeningChartData.categories}
                    series={screeningChartData.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    goalLabelOffsetX={0}
                    min={0}
                    max={100}
                    tickAmount={20}
                    markers={false}
                    yAxisLabel="PMH Screening (%)"
                    xAxisLabel="Time Period"
                    colors={["#0072B2", "#D55E00"]}
                    fontFamily="Poppins, Segoe UI, sans-serif"
                    legendPosition="top"
                    legendAlign="center"
                    showXAxisTooltip={true}
                    noDataMessage="No Data Available"
                    exportFilename="PMH Screening"
                    chartTitle="PMH Screening"
                    chartSubtitle="Percent of sample patient charts that had documentation of PMH screening using a validated tool."
                    pageTitle="Outcome Measures"
                    isLoading={loading}
                    yAxisDecimals={1}
                    decimals={2}
                    xAxisLabelFontSize="14px"
                />
            </div>
        </div>
    );
};

export default OutcomeMeasures;
